// WP-7.1 (M30 SM-1..4, LOCKED §2.35, S-33 `/simulator`): scenario simulator hooks.
// `useRunScenario` mirrors lib/affordability.ts's `useCheckAffordability` — a `useMutation`
// wrapping the request, not a `useQuery`, because running a scenario is an explicit,
// disposable, on-demand action (SM-2: never a write, but still "fetch triggered by a click"
// rather than cache-driven background data). The saved-scenario list/save/delete triplet
// mirrors lib/intel.ts's saved-views triplet — the same opaque jsonb-config shape (SM-4 "MAY
// be saved/reloaded").
import {
  runScenarioRequestSchema,
  savedScenarioListResponseSchema,
  savedScenarioSchema,
  scenarioResultResponseSchema,
  type RunScenarioRequest,
  type SavedScenario,
  type SavedScenarioCreateRequest,
  type ScenarioResultResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export function useRunScenario(): UseMutationResult<
  ScenarioResultResponse,
  Error,
  RunScenarioRequest
> {
  return useMutation({
    mutationFn: async (input) => {
      const body = runScenarioRequestSchema.parse(input);
      return scenarioResultResponseSchema.parse(await api.post('/api/v1/simulator/run', body));
    },
  });
}

const SAVED_SCENARIOS_KEY = ['simulator', 'scenarios'] as const;

export function useSavedScenarios(): UseQueryResult<SavedScenario[]> {
  return useQuery({
    queryKey: SAVED_SCENARIOS_KEY,
    queryFn: async () =>
      savedScenarioListResponseSchema.parse(await api.get('/api/v1/simulator/scenarios')).scenarios,
  });
}

export function useSaveScenario(): UseMutationResult<
  SavedScenario,
  Error,
  SavedScenarioCreateRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      savedScenarioSchema.parse(await api.post('/api/v1/simulator/scenarios', input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SAVED_SCENARIOS_KEY });
    },
  });
}

// Parameterized by id (mirrors lib/goals.ts's `useReleaseReserve(id)`) so a per-row component
// can own its own mutation instance — the house pattern for a row with its own pending state.
export function useDeleteScenario(id: string): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete(`/api/v1/simulator/scenarios/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SAVED_SCENARIOS_KEY });
    },
  });
}
